import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import React, { useState, useEffect } from 'react';
import { FormattedDate } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import Box, { BoxProps } from 'ui-box';
import {
  Button,
  Card,
  ControlGroup,
  defaultFills,
  EmptyInputGroup,
  Heading,
  InputGroup,
  majorScale,
  Menu,
  MenuItem,
  minorScale,
  NumberInput,
  Popover,
  Spinner,
  TextArea,
  useAlert,
  useToast,
} from '../../npaper';
import { DateInput } from '../../npaper/DateInput';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { Portal } from '../../npaper/Portal';
import { UserSelect } from '../../npaper/UserSelect';
import { ITask, ITaskLog, TaskStatus, useStores } from '../../stores';
import { CompanyThumbnail } from '../companies';
import { ContactThumbnail } from '../contacts';
import { ContactSelect } from '../contacts/ContactSelect';
import { ProductThumbnail } from '../products/ProductThumbnail';
import { SaleThumbnail } from '../sales';
import { UserThumbnail } from '../settings/UserThumbnail';
import { TaskCardHeader } from './TaskCard';
import { TaskLogTracking } from './TaskLogTracking';
import styles from './TaskSideSheet.module.scss';

export interface ITaskSideSheetProps {
  isShown: boolean;
  onClose: () => void;
  onOpened?: () => void;
  task: ITask;
}

export const TaskSideSheet: React.FC<ITaskSideSheetProps> = observer(
  ({ isShown, onClose, onOpened, task }) => {
    const { taskStore, saleStore } = useStores();

    const history = useHistory();
    const toaster = useToast();

    const overlayRef = React.useRef(null);
    const [shown, setShown] = useState(false);

    const createSaleAlert = useAlert({
      title: 'Opret salg fra opgaven',
      icon: 'dollar',
      intent: 'success',
      message:
        'Vær opmærksom på at det er det faktiske forbrug, og ikke budgettet, der anvendes på salget. Alle andre oplysninger overføres automatisk.',
      confirmButtonText: 'Opret salg',
    });

    useEffect(() => {
      setShown(isShown);
    }, [isShown]);

    /**
     * Handle escape press; should close on escale
     */
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [onClose]);

    const createSaleFromTask = async () => {
      try {
        // Create a sale with this task in an item
        const snapshot = getSnapshot(task);
        const sale = await saleStore.createItem({
          title: snapshot.title,
          customer: snapshot.customer, // This should match - the api will check this nevertheless.
          task: snapshot.id,
        } as any);
        // Update the task...
        taskStore.getItem(task.id, true);
        // Route to sale page
        history.push(`/sales/${sale.id}`);
      } catch (error) {
        // TODO: Handle error - tell the user that we failed ^_^
        toaster.showDefaultError(error);
        throw error;
      }
    };

    const statusTitle =
      task.status === TaskStatus.CREATED
        ? 'To do'
        : task.status === TaskStatus.PROCESSING
        ? 'Igangværrende'
        : task.status === TaskStatus.PAUSED
        ? 'På hold'
        : 'Færdig';

    return (
      <Portal>
        <CSSTransition
          in={shown}
          timeout={150}
          className={styles.taskSheet}
          onExited={() => {
            onClose();
          }}
          onEntered={() => {
            onOpened && onOpened();
          }}
          unmountOnExit={true}
          nodeRef={overlayRef}
        >
          <div ref={overlayRef}>
            <div className={styles.taskSheetContainer}>
              <Button
                width={majorScale(4)}
                height={majorScale(4)}
                borderRadius="100%"
                marginBottom={majorScale(-2)}
                border={`2px solid ${defaultFills.normal[100]}`}
                backgroundColor="white"
                position="relative"
                zIndex={2}
                marginX="auto"
                iconLeft="chevron-down"
                onClick={() => setShown(false)}
              />
              <Card
                elevation={2}
                border="none"
                backgroundColor="white"
                position="relative"
                maxHeight="100%"
              >
                <TaskCardHeader
                  task={task}
                  showTracking
                  padding={majorScale(2)}
                  borderBottom={`2px solid ${defaultFills.normal[100]}`}
                  position="relative"
                  zIndex={1}
                />

                <Box className={styles.scrollContainer}>
                  <Box
                    padding={majorScale(2)}
                    borderBottom={`1px solid ${defaultFills.normal[100]}`}
                  >
                    <Heading size={300} marginBottom={majorScale(2)}>
                      Detaljer
                    </Heading>

                    <Box marginTop={majorScale(2)} display="flex">
                      <Box>
                        <Heading size={100} marginBottom={majorScale(1)}>
                          Serivceydelse
                        </Heading>
                        {task.service ? (
                          <Box width="fit-content">
                            <ProductThumbnail product={task.service} />
                          </Box>
                        ) : (
                          <Spinner size={16} />
                        )}
                      </Box>
                      <Box textAlign="right" marginLeft="auto">
                        <Heading size={100} marginBottom={majorScale(1)}>
                          Status
                        </Heading>
                        <Popover
                          disabled={task.status === TaskStatus.DONE}
                          content={(close) => (
                            <Menu>
                              <MenuItem
                                icon="play"
                                text="Igangværende"
                                intent="info"
                                onClick={() => {
                                  close();
                                  taskStore.updateItem(task, {
                                    status: TaskStatus.PROCESSING,
                                  });
                                }}
                              />
                              <MenuItem
                                icon="pause"
                                text="På hold"
                                intent="warning"
                                onClick={() => {
                                  close();
                                  taskStore.updateItem(task, {
                                    status: TaskStatus.PAUSED,
                                  });
                                }}
                              />
                              <MenuItem
                                icon="endorsed"
                                text="Færdig"
                                intent="success"
                                onClick={() => {
                                  close();
                                  taskStore.updateItem(task, {
                                    status: TaskStatus.DONE,
                                  });
                                }}
                              />
                            </Menu>
                          )}
                        >
                          <Button>{statusTitle}</Button>
                        </Popover>
                      </Box>
                    </Box>

                    <Box marginTop={majorScale(2)} display="flex">
                      <Box>
                        <Heading size={100} marginBottom={majorScale(1)}>
                          Tidsfrist
                        </Heading>
                        <DateInput
                          placeholder="Deadline"
                          onChange={(date) =>
                            taskStore.updateItem(task, { due_date: date })
                          }
                        >
                          <Button iconLeft="flag">
                            {task.due_date ? (
                              <FormattedDate
                                format="default"
                                value={task.due_date}
                              />
                            ) : (
                              'Tilføj deadline'
                            )}
                          </Button>
                        </DateInput>
                      </Box>

                      <Box marginLeft="auto" textAlign="right">
                        <Heading size={100} marginBottom={majorScale(1)}>
                          Budget
                        </Heading>
                        <ControlGroup>
                          <NumberInput
                            maxWidth={90}
                            placeholder="Budget"
                            maximumFractionDigits={2}
                            minimumFractionDigits={0}
                            defaultValue={
                              task.budget === null ? undefined : task.budget
                            }
                            onChange={(budget) =>
                              taskStore.updateItem(task, { budget })
                            }
                          />
                          <EmptyInputGroup>
                            {task.service && (
                              <FormattedUnit
                                unit={task.service.unit}
                                unitDisplay="long"
                              />
                            )}
                          </EmptyInputGroup>
                        </ControlGroup>
                      </Box>
                    </Box>

                    <Box marginTop={majorScale(2)}>
                      <Heading size={100} marginBottom={majorScale(1)}>
                        Titel
                      </Heading>
                      <InputGroup
                        placeholder="Opgavens titel"
                        defaultValue={task.title}
                        onBlur={(e) =>
                          taskStore.updateItem(task, { title: e.target.value })
                        }
                      />
                    </Box>

                    <Box marginTop={majorScale(2)}>
                      <Heading size={100} marginBottom={majorScale(1)}>
                        Beskrivelse
                      </Heading>
                      <TextArea
                        placeholder="En kort beskrivelse af opgaven"
                        defaultValue={task.description || ''}
                        maxLength={500}
                        hideHint
                        onBlur={(e) =>
                          taskStore.updateItem(task, {
                            description: e.target.value,
                          })
                        }
                      />
                    </Box>
                  </Box>
                  <Box
                    padding={majorScale(2)}
                    borderBottom={`1px solid ${defaultFills.normal[100]}`}
                  >
                    <Heading size={300} marginBottom={majorScale(2)}>
                      Salg
                    </Heading>

                    <Box marginTop={majorScale(2)}>
                      <Heading size={100} marginBottom={majorScale(1)}>
                        Kunde
                      </Heading>
                      {task.customer ? (
                        <CompanyThumbnail company={task.customer} />
                      ) : (
                        <Spinner size={16} />
                      )}
                    </Box>

                    <Box marginTop={majorScale(2)}>
                      <Heading size={100} marginBottom={majorScale(1)}>
                        Relateret salg
                      </Heading>
                      {task.sale ? (
                        <SaleThumbnail sale={task.sale} />
                      ) : task.sale === undefined ? (
                        <Spinner size={16} />
                      ) : (
                        <>
                          <Button
                            iconLeft="add"
                            onClick={() => {
                              // TODO: Create a sale with this line.
                              createSaleAlert.show(() => createSaleFromTask());
                            }}
                          >
                            Opret tilbud
                          </Button>
                        </>
                      )}
                    </Box>

                    <Box marginTop={majorScale(2)}>
                      <Heading size={100} marginBottom={majorScale(1)}>
                        Kontaktpersoner
                      </Heading>
                      {task.contacts.map((contact, i) =>
                        contact ? (
                          <Box
                            key={contact.id}
                            display="flex"
                            alignItems="center"
                          >
                            <ContactThumbnail contact={contact} />
                            <Button
                              minimal
                              iconLeft="trash"
                              marginLeft="auto"
                              onClick={() => task.removeContact(contact)}
                            />
                          </Box>
                        ) : (
                          <Spinner
                            size={16}
                            key={'index' + i}
                            marginTop={majorScale(1)}
                            display="block"
                          />
                        )
                      )}
                      <ContactSelect
                        filter={{
                          company: task.customer ? task.customer.id : undefined,
                          id__exclude: getSnapshot(task).contacts,
                        }}
                        onSelect={(contact) => task.addContact(contact)}
                      >
                        <Button iconLeft="add" marginTop={majorScale(1)}>
                          Tilføj
                        </Button>
                      </ContactSelect>
                    </Box>
                  </Box>
                  <Box
                    padding={majorScale(2)}
                    borderBottom={`1px solid ${defaultFills.normal[100]}`}
                  >
                    <Heading size={300} marginBottom={majorScale(2)}>
                      Ansvarlige
                    </Heading>

                    {task.responsible.map((member, i) =>
                      member ? (
                        <Box display="flex" alignItems="center" key={member.id}>
                          <UserThumbnail
                            user={member.user}
                            key={member.id}
                            marginRight={majorScale(1)}
                          />
                          <Box>
                            <p className="medium">{member.user.name}</p>
                            <p className="muted small">{member.user.email}</p>
                          </Box>
                          <Button
                            minimal
                            iconLeft="trash"
                            marginLeft="auto"
                            onClick={() => task.removeResponsible(member)}
                          />
                        </Box>
                      ) : (
                        <Spinner size={16} key={'index-' + i} />
                      )
                    )}
                    <UserSelect
                      onSelect={(user) => task.addResponsible(user)}
                      filter={{ id__exclude: getSnapshot(task).responsible }}
                    >
                      <Button iconLeft="add" marginTop={majorScale(2)}>
                        Tilføj
                      </Button>
                    </UserSelect>
                  </Box>

                  <Box padding={majorScale(2)}>
                    <Heading size={300} marginBottom={majorScale(2)}>
                      Log
                    </Heading>
                    {task.logs.map((log, i) =>
                      log ? (
                        <TaskLog log={log} key={i} />
                      ) : (
                        <Spinner size={16} key={'index-' + i} />
                      )
                    )}
                    {task.logs.length === 0 && (
                      <Box is="p" className="muted" marginTop={minorScale(-3)}>
                        Der er ikke tilføjet noget til loggen endnu
                      </Box>
                    )}
                  </Box>
                </Box>
              </Card>
            </div>
          </div>
        </CSSTransition>
      </Portal>
    );
  }
);

const TaskLog = observer(
  ({ log, ...props }: { log: ITaskLog } & BoxProps<'div'>) => {
    return (
      <Box
        {...props}
        display="flex"
        alignItems="flex-start"
        marginTop={majorScale(2)}
      >
        {log.logged_by && (
          <UserThumbnail
            user={log.logged_by.user}
            marginRight={majorScale(1)}
          />
        )}
        <Box>
          {log.logged_by && (
            <Box>
              <span className="p medium">{log.logged_by.user.name}</span>{' '}
              <span className="p muted small">
                <FormattedDate format="default" value={log.created_at} />
              </span>
            </Box>
          )}
          {log.note && <Box is="p">{log.note}</Box>}
          <Box display="flex" alignItems="center" marginTop={minorScale(1)}>
            <TaskLogTracking log={log} />
          </Box>
        </Box>
      </Box>
    );
  }
);
